import React, { useState, useEffect, useRef } from 'react';
import { Code, Eye, Play } from 'react-feather';
import { ConfigEmptyState } from '../ConfigPages';
import { EtlSection, EtlBanner, EtlKv, EtlTiles } from '../EtlPages';


const SchemaPreview = ({ schemaConfig, extractedData, aiExtractedData, emailConfig, onSave, onBack, isLoading, error, hideFooter = false, onFooterChange }) => {
  const [previewData, setPreviewData] = useState({ revenue: [], roomStats: [] });
  const [validationErrors, setValidationErrors] = useState([]);
  const [testResults, setTestResults] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');

  // Check if we have AI-extracted database-ready data
  const hasAiExtractedData = aiExtractedData?.confirmed && aiExtractedData?.extractedData;
  const exportSchemaRef = useRef(() => {});

  useEffect(() => {
    if (!hasAiExtractedData) {
      generatePreviewData();
    }
  }, [schemaConfig, extractedData, hasAiExtractedData]);

  const generatePreviewData = () => {
    const preview = { revenue: [], roomStats: [] };
    const errors = [];

    Object.entries(schemaConfig.fieldMappings).forEach(([category, mapping]) => {
      if (!mapping.sourceTable || mapping.columnMappings.length === 0) {
        return;
      }

      const tableIndex = parseInt(mapping.sourceTable);
      const table = extractedData?.tables?.[tableIndex];
      
      if (!table || !table.data) {
        errors.push(`Table ${tableIndex + 1} not found for ${category}`);
        return;
      }

      // Get data rows based on row range
      let dataRows = table.data.slice(1); // Skip header
      
      if (mapping.rowRange.type === 'range') {
        const start = (mapping.rowRange.startRow || 1) - 1;
        const end = mapping.rowRange.endRow || dataRows.length;
        dataRows = dataRows.slice(start, end);
      } else if (mapping.rowRange.type === 'keyword') {
        const startKeyword = mapping.rowRange.startKeyword?.toLowerCase();
        const endKeyword = mapping.rowRange.endKeyword?.toLowerCase();
        
        if (startKeyword && endKeyword) {
          let startIndex = -1;
          let endIndex = dataRows.length;
          
          dataRows.forEach((row, index) => {
            const rowText = row.join(' ').toLowerCase();
            if (startIndex === -1 && rowText.includes(startKeyword)) {
              startIndex = index;
            }
            if (startIndex !== -1 && rowText.includes(endKeyword)) {
              endIndex = index + 1;
            }
          });
          
          if (startIndex !== -1) {
            dataRows = dataRows.slice(startIndex, endIndex);
          }
        }
      }

      // Transform data according to mappings
      const transformedRows = dataRows.map((row, rowIndex) => {
        const transformedRow = {
          locationId: schemaConfig.hotelId,
          Date: new Date().toISOString().split('T')[0], // Default to today
          _sourceRow: rowIndex + 1
        };

        mapping.columnMappings.forEach(colMapping => {
          if (colMapping.sourceCol !== '' && colMapping.targetField) {
            const sourceValue = row[parseInt(colMapping.sourceCol)] || '';
            transformedRow[colMapping.targetField] = transformValue(sourceValue, colMapping.transform);
          }
        });

        return transformedRow;
      }).filter(row => 
        // Filter out rows that don't have required fields
        row.Type && row.Current
      );

      preview[category] = transformedRows;

      // Validate mappings
      const requiredFields = category === 'revenue' 
        ? ['Type', 'Current'] 
        : ['Type', 'Current'];
      
      const mappedFields = mapping.columnMappings.map(m => m.targetField).filter(Boolean);
      const missingFields = requiredFields.filter(field => !mappedFields.includes(field));
      
      if (missingFields.length > 0) {
        errors.push(`${category}: Missing required fields: ${missingFields.join(', ')}`);
      }
    });

    setPreviewData(preview);
    setValidationErrors(errors);
  };

  const transformValue = (value, transform) => {
    if (!value || transform === 'none') return value;

    try {
      switch (transform) {
        case 'currency':
          return parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
        case 'number':
          return parseFloat(value) || 0;
        case 'date':
          return new Date(value).toISOString().split('T')[0];
        case 'text':
          return value.toString().trim();
        case 'uppercase':
          return value.toString().toUpperCase();
        case 'lowercase':
          return value.toString().toLowerCase();
        default:
          return value;
      }
    } catch (error) {
      return value;
    }
  };

  const runTest = () => {
    setTestResults({
      status: 'running',
      message: 'Testing schema mappings...'
    });

    // Simulate test run
    setTimeout(() => {
      const totalRecords = previewData.revenue.length + previewData.roomStats.length;
      const hasErrors = validationErrors.length > 0;
      
      setTestResults({
        status: hasErrors ? 'warning' : 'success',
        message: hasErrors 
          ? `Test completed with ${validationErrors.length} warning(s). Schema can be saved but may need refinement.`
          : `Test passed! Schema will process ${totalRecords} records successfully.`,
        details: {
          revenueRecords: previewData.revenue.length,
          roomStatRecords: previewData.roomStats.length,
          validationErrors: validationErrors.length
        }
      });
    }, 2000);
  };

  const exportSchema = () => {
    const exportData = {
      ...schemaConfig,
      createdAt: new Date().toISOString(),
      preview: previewData,
      validation: {
        errors: validationErrors,
        recordCounts: {
          revenue: previewData.revenue.length,
          roomStats: previewData.roomStats.length
        }
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schema-${schemaConfig.hotelId || 'new'}-${schemaConfig.fileType || 'config'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  exportSchemaRef.current = exportSchema;

  /**
   * Publish the Export action to the wizard frame. Save is the frame's
   * primary button — this step only contributes the secondary.
   */
  useEffect(() => {
    if (!onFooterChange) return;
    onFooterChange({
      secondary: (
        <button type="button" className="cp-btn" onClick={exportSchemaRef.current}>
          {hasAiExtractedData ? 'Export data' : 'Export config'}
        </button>
      ),
    });
  }, [onFooterChange, hasAiExtractedData]);



  // Render AI extracted revenue data

  // Render AI extracted room stats

  // Render AI extracted occupancy data. New shape is one row PER stay date
  // (hotelOccupancy fields); legacy single-object results render as one row.
  // Mirrors OccupancyCard in AiExtractionTab.js.

  /** A generic preview table — first five rows, with the true total stated. */
  const renderPreviewTable = (data, title) => {
    if (!data || data.length === 0) {
      return (
        <ConfigEmptyState
          title={`No ${title.toLowerCase()} would be produced`}
          body="The current mappings generate no rows for this destination. Go back to Field mapping and check the source table and row range."
        />
      );
    }
    const columns = Object.keys(data[0]).filter((key) => !key.startsWith('_'));
    return (
      <EtlSection title={title} description={`${data.length.toLocaleString()} records`}>
        <div className="it-frame">
          <div className="it-scroll" style={{ '--it-max-h': '44vh' }}>
            <table className="it-tbl">
              <thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {data.slice(0, 5).map((row, index) => (
                  <tr key={index} className="cp-row it-row-hover">
                    {columns.map((c) => (
                      <td key={c} className="cp-cell">
                        <span className="cp-mono">
                          {typeof row[c] === 'number' ? row[c].toLocaleString() : row[c]}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.length > 5 ? (
            <div className="ep-pager"><span className="cp-meta">Showing 5 of {data.length.toLocaleString()}</span></div>
          ) : null}
        </div>
      </EtlSection>
    );
  };

  const money = (v) => (v != null ? `$${Number(v).toLocaleString()}` : '—');

  const renderAiRevenueTable = (data) => {
    if (!data || data.length === 0) return <ConfigEmptyState title="No revenue extracted" body="The model found no revenue lines in this document." />;
    return (
      <EtlSection title="Revenue" description={`${data.length} categories`}>
        <div className="it-frame">
          <div className="it-scroll" style={{ '--it-max-h': '44vh' }}>
            <table className="it-tbl">
              <thead>
                <tr>
                  <th>Category</th>
                  <th style={{ textAlign: 'right' }}>Today</th>
                  <th style={{ textAlign: 'right' }}>MTD</th>
                  <th style={{ textAlign: 'right' }}>YTD</th>
                  <th style={{ textAlign: 'right' }}>Budget</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => (
                  <tr key={idx} className="cp-row it-row-hover">
                    <td className="cp-cell"><span className="cp-name">{row.revenueTypeName}</span></td>
                    {['currentValue', 'mtd', 'ytd', 'budgetAmount'].map((k) => (
                      <td key={k} className="cp-cell" style={{ textAlign: 'right' }}>
                        <span className="cp-mono">{money(row[k])}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </EtlSection>
    );
  };

  const renderAiRoomStats = (data) => {
    if (!data) return null;
    const stats = [
      { label: 'Rooms available', value: data.roomsAvailable },
      { label: 'Rooms occupied', value: data.roomsOccupied },
      { label: 'Occupancy', value: data.occupancyPercent != null ? `${data.occupancyPercent}%` : null },
      { label: 'ADR', value: data.adr != null ? `$${data.adr.toFixed(2)}` : null },
      { label: 'RevPAR', value: data.revPAR != null ? `$${data.revPAR.toFixed(2)}` : null },
      { label: 'Room revenue', value: money(data.roomRevenue) === '—' ? null : money(data.roomRevenue) },
      { label: 'Arrivals', value: data.arrivals },
      { label: 'Departures', value: data.departures },
      { label: 'Stayovers', value: data.stayovers },
      { label: 'No-shows', value: data.noShows },
      { label: 'Total guests', value: data.totalGuests },
    ].filter((s) => s.value != null);
    if (!stats.length) return null;
    return (
      <EtlSection title="Room statistics">
        <div className="ep-panel">
          <EtlKv rows={stats.map((s) => ({ label: s.label, value: String(s.value), mono: true }))} />
        </div>
      </EtlSection>
    );
  };

  /**
   * Occupancy. The current shape is one row PER stay date (hotelOccupancy
   * fields); a legacy single-object result renders as one row. Mirrors
   * OccupancyCard in AiExtractionTab.
   */
  const renderAiOccupancy = (data) => {
    if (!data || (Array.isArray(data) && data.length === 0)) return null;
    const rows = Array.isArray(data) ? data : [data];
    const columns = [
      { key: 'occupancyDate', label: 'Stay date' },
      { key: 'totalRooms', label: 'Total rooms' },
      { key: 'availableRooms', label: 'Available' },
      { key: 'otbGrpRooms', label: 'OTB grp' },
      { key: 'otbTrnRooms', label: 'OTB trn' },
      { key: 'prjGroupRooms', label: 'Prj grp' },
      { key: 'prjTrnRooms', label: 'Prj trn' },
      { key: 'otbGrpRevenue', label: 'OTB grp $', currency: true },
      { key: 'otbTrnRevenue', label: 'OTB trn $', currency: true },
      { key: 'totalRevenue', label: 'Total $', currency: true },
    ].filter((col) => rows.some((r) => r && r[col.key] != null));
    if (!columns.length) return null;
    return (
      <EtlSection title="Occupancy" description={rows.length > 1 ? `${rows.length} stay dates` : undefined}>
        <div className="it-frame">
          <div className="it-scroll" style={{ '--it-max-h': '44vh' }}>
            <table className="it-tbl">
              <thead><tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx} className="cp-row it-row-hover">
                    {columns.map((c) => (
                      <td key={c.key} className="cp-cell">
                        <span className="cp-mono">
                          {row?.[c.key] == null ? '—' : (c.currency ? money(row[c.key]) : String(row[c.key]))}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </EtlSection>
    );
  };

  const tabs = [
    { id: 'preview', label: hasAiExtractedData ? 'Extracted data' : 'Preview', icon: Eye },
    { id: 'config', label: hasAiExtractedData ? 'Extraction info' : 'Raw config', icon: Code },
    { id: 'test', label: 'Test', icon: Play },
  ];

  return (
    <>
      <div className="cp-toolbar">
        <div className="cp-tabs" role="tablist" aria-label="Review views">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={activeTab === tab.id ? 'cp-active' : ''}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="cp-toolbar-spacer" />
        {hasAiExtractedData && aiExtractedData.businessDate ? (
          <div className="cp-meta">Business date {aiExtractedData.businessDate}</div>
        ) : null}
      </div>

      <div style={{ padding: 18 }}>
        {schemaConfig.fieldMappings?.roomStats?.useNormalized ? (
          <div style={{ marginBottom: 16 }}>
            <EtlBanner tone="ok" title="Normalized room stats">
              Statistics land in <code>hotelRoomStatsNormalized</code> — one row per stat type, so a new
              metric needs a mapping rather than a schema change.
            </EtlBanner>
          </div>
        ) : null}

        {validationErrors.length > 0 ? (
          <div style={{ marginBottom: 16 }}>
            <EtlBanner tone="warn" title="Configuration warnings">
              <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </EtlBanner>
          </div>
        ) : null}

        {error ? (
          <div style={{ marginBottom: 16 }}>
            <EtlBanner tone="error" title="Could not save">{error}</EtlBanner>
          </div>
        ) : null}

        {activeTab === 'preview' ? (
          hasAiExtractedData ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <EtlBanner tone="ok" title="Database-ready">
                  The model produced rows that can be written directly, so manual field mapping is skipped.
                  {aiExtractedData.dataTypes?.length ? ` Covers: ${aiExtractedData.dataTypes.join(', ')}.` : ''}
                </EtlBanner>
              </div>
              {aiExtractedData.extractedData?.revenue ? renderAiRevenueTable(aiExtractedData.extractedData.revenue) : null}
              {aiExtractedData.extractedData?.roomStats ? renderAiRoomStats(aiExtractedData.extractedData.roomStats) : null}
              {aiExtractedData.extractedData?.occupancy ? renderAiOccupancy(aiExtractedData.extractedData.occupancy) : null}
            </>
          ) : (
            <>
              {renderPreviewTable(previewData.revenue, 'Revenue')}
              {renderPreviewTable(previewData.roomStats, 'Room statistics')}
            </>
          )
        ) : null}

        {activeTab === 'config' ? (
          <>
            {emailConfig ? (
              <EtlSection title="Recognition">
                <div className="ep-panel">
                  <EtlKv
                    rows={[
                      { label: 'Checks', value: emailConfig.emailMatchType === 'file' ? 'Attachment filename' : 'Email subject' },
                      {
                        label: 'Keyword',
                        value: [
                          emailConfig.auditFile,
                          ...(Array.isArray(emailConfig.keywords) ? emailConfig.keywords : []),
                        ].filter(Boolean).join(' or '),
                        mono: true,
                      },
                      { label: 'Mode', value: emailConfig.matchMode === 'exact' ? 'Exact' : 'Contains' },
                      { label: 'Never', value: (emailConfig.excludeKeywords || []).join(', '), mono: true },
                      { label: 'Formats', value: (emailConfig.allowedExtensions || []).join(', '), mono: true },
                      { label: 'Type', value: emailConfig.fileType === 'custom' ? emailConfig.customFileType : emailConfig.fileType, mono: true },
                    ]}
                  />
                </div>
              </EtlSection>
            ) : null}

            {hasAiExtractedData && aiExtractedData.documentInfo ? (
              <EtlSection title="Extraction">
                <div className="ep-panel">
                  <EtlKv
                    rows={[
                      { label: 'Run id', value: aiExtractedData.extractionId, mono: true },
                      { label: 'Document', value: aiExtractedData.documentInfo.documentType },
                      { label: 'Property', value: aiExtractedData.documentInfo.propertyName },
                      { label: 'Covers', value: aiExtractedData.dataTypes?.join(', ') },
                    ]}
                  />
                </div>
              </EtlSection>
            ) : null}

            <EtlSection title={hasAiExtractedData ? 'Extracted data (JSON)' : 'Full configuration (JSON)'}>
              <pre className="ep-raw">
                {hasAiExtractedData
                  ? JSON.stringify(aiExtractedData.extractedData, null, 2)
                  : JSON.stringify({ schemaConfig, emailIntegration: emailConfig }, null, 2)}
              </pre>
            </EtlSection>
          </>
        ) : null}

        {activeTab === 'test' ? (
          <EtlSection
            title="Dry run"
            description="Applies the mappings to the sample file and counts what would be written. Nothing is imported."
            actions={(
              <button type="button" className="cp-btn cp-primary" onClick={runTest} disabled={testResults?.status === 'running'}>
                {testResults?.status === 'running' ? 'Running…' : 'Run test'}
              </button>
            )}
          >
            {testResults ? (
              <>
                <EtlBanner tone={testResults.status === 'success' ? 'ok' : testResults.status === 'warning' ? 'warn' : 'info'}>
                  {testResults.message}
                </EtlBanner>
                {testResults.status !== 'running' ? (
                  <div style={{ marginTop: 12 }}>
                    <EtlTiles
                      tiles={[
                        { key: 'rev', label: 'Revenue rows', n: previewData.revenue.length },
                        { key: 'stats', label: 'Room stat rows', n: previewData.roomStats.length },
                        { key: 'issues', label: 'Validation issues', n: validationErrors.length, tone: validationErrors.length ? 'warn' : undefined },
                      ]}
                    />
                  </div>
                ) : null}
              </>
            ) : (
              <div className="cp-help">Not run yet.</div>
            )}
          </EtlSection>
        ) : null}
      </div>

      {/* Navigation lives in the wizard frame; kept for standalone callers. */}
      <div className={`justify-between items-center ${hideFooter ? 'hidden' : 'flex'}`} style={{ padding: '0 18px 18px', gap: 8 }}>
        <button type="button" className="cp-btn" onClick={onBack}>Back</button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="cp-btn" onClick={exportSchema}>
            {hasAiExtractedData ? 'Export data' : 'Export config'}
          </button>
          <button type="button" className="cp-btn cp-primary" onClick={onSave} disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

export default SchemaPreview;
